import { useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import moment from "moment";

import {
  FloatingPortal,
  flip,
  safePolygon,
  useDelayGroup,
  useFloating,
  useHover,
  useInteractions,
  type Placement,
} from "@floating-ui/react";
import { constants } from "@north-beam/nb-common";

interface Props {
  compareDateRange: constants.CompareDateRange | null;
  placement?: Placement;
  selectSamePeriodDifferentYear(calendar: "standard", year: number): void;
}

export const SamePeriodDifferentYearSubmenu = ({
  compareDateRange,
  placement,
  selectSamePeriodDifferentYear,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    transform: false,
    placement: placement ?? "right-start",
    middleware: [flip()],
  });

  const { delay } = useDelayGroup(context);

  const hover = useHover(context, {
    delay,
    handleClose: safePolygon({
      requireIntent: false,
      blockPointerEvents: true,
      buffer: 1,
    }),
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  return (
    <div>
      <div
        className={clsx(
          "hover:cursor-pointer hover:bg-cosmic-4 hover:font-semibold",
          "px-4 py-1.5 flex items-center justify-between",
          { "bg-cosmic-3 font-semibold": isOpen },
        )}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <span className="text-interstellar text-sm leading-loose">
          Same period, different year
        </span>
        <i className="fa-regular fa-sharp fa-chevron-right text-cosmic text-xs" />
      </div>
      <FloatingPortal>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              transition={{ ease: "easeOut", duration: 0.2 }}
              initial={{ opacity: 0, translateY: "0.25rem" }}
              animate={{ opacity: 1, translateY: 0 }}
              exit={{ opacity: 0, translateY: "0.25rem" }}
              className="rounded-md bg-white elevation-1 py-2 z-[2003] text-sm w-56"
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              {yearSelectOptions().map((year) => (
                <div
                  key={year}
                  onClick={() =>
                    selectSamePeriodDifferentYear("standard", year)
                  }
                  className={clsx(
                    "py-2 px-4 hover:bg-cosmic-4 hover:font-semibold cursor-pointer",
                    {
                      "bg-cosmic-3 font-semibold pointer-events-none":
                        compareDateRange &&
                        constants.isCompareDateRangeOfType(
                          compareDateRange,
                          constants.CompareDateRangeTypes
                            .SAMEPERIODDIFFERENTYEAR,
                        ) &&
                        compareDateRange.year === year,
                    },
                  )}
                >
                  {year}
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </FloatingPortal>
    </div>
  );
};

function yearSelectOptions() {
  const today = moment();
  const options = [];

  for (let i = today.year() - 1; i >= 2019; i--) {
    options.push(i);
  }

  return options;
}
